/**
 * Répartition analytique (V1) + Comptabilité V2 transitoire (iter31) pour les factures fournisseurs.
 *
 * Extrait de FactureFournisseurDetail.jsx pour limiter la taille de ce dernier
 * et regrouper toute la logique de répartition / V2 dans un composant dédié.
 *
 * V1 (RepartitionFFSection)  : ventile le TTC sur plusieurs mois pour les dashboards (sans toucher
 *                              la compta — 1 écriture = 1 date, CO 957).
 * V2 (V2TransitoireToggle)   : matérialise réellement la répartition via le compte 1300
 *                              « Actifs transitoires » + N OD pending mensuelles. Opt-in par FF et
 *                              gouverné par un flag global `config.transitoire`.
 */
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar, Loader2 } from "lucide-react";
import api from "@/lib/api";

const fmtIso = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleDateString("fr-CH");
  } catch {
    return s;
  }
};

// ============================================================
// V1 — Répartition analytique (dashboards uniquement)
// ============================================================
export default function FFRepartitionSection({ ff, onUpdated }) {
  const [showConfig, setShowConfig] = useState(false);
  const [type, setType] = useState(ff.repartition_analytique?.type || "annuel");
  const [dateDebut, setDateDebut] = useState(
    ff.repartition_analytique?.date_debut || ff.date_facture || ""
  );
  const [dateFin, setDateFin] = useState(ff.repartition_analytique?.date_fin || "");
  const [saving, setSaving] = useState(false);
  const rep = ff.repartition_analytique;
  const actif = rep?.actif;

  const apply = async () => {
    if (!dateDebut) {
      toast.error("Date de début obligatoire");
      return;
    }
    if (type === "personnalise" && !dateFin) {
      toast.error("Date de fin obligatoire pour 'Personnalisé'");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/factures-fournisseurs/${ff.id}/repartition`, {
        actif: true,
        type,
        date_debut: dateDebut,
        date_fin: type === "personnalise" ? dateFin : null,
      });
      toast.success("Répartition analytique configurée");
      setShowConfig(false);
      onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const disable = async () => {
    if (!window.confirm(
      "Désactiver la répartition analytique ?\n\nLe montant TTC sera réimputé sur le mois de la facture dans les statistiques."
    )) return;
    setSaving(true);
    try {
      await api.put(`/factures-fournisseurs/${ff.id}/repartition`, { actif: false });
      toast.success("Répartition désactivée");
      onUpdated();
    } catch {
      toast.error("Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="ff-repartition-section">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h3 className="font-heading font-semibold text-gray-900 flex items-center gap-1.5">
            <Calendar className="w-4 h-4 text-indigo-600" /> Répartition analytique
          </h3>
          <p className="text-[11px] text-gray-500 mt-0.5">
            Ventile le montant TTC sur plusieurs mois pour les dashboards (sans modifier la compta).
          </p>
        </div>
        {actif ? (
          <Badge className="bg-purple-100 text-purple-800 border-purple-300 text-[10px]">
            Active · {rep.type}
          </Badge>
        ) : (
          <Badge variant="outline" className="text-[10px] text-gray-500">
            Non répartie
          </Badge>
        )}
      </div>
      {actif ? (
        <div className="bg-purple-50 border border-purple-200 rounded p-3 text-sm">
          <p className="text-[11px] text-purple-700 mb-1">
            Du <strong>{fmtIso(rep.date_debut)}</strong> au <strong>{fmtIso(rep.date_fin)}</strong> ·
            {" "}
            <strong>{rep.montants_mensuels?.length}</strong> mois
          </p>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-1 mt-2 text-[10px] font-mono">
            {rep.montants_mensuels?.slice(0, 12).map((e) => (
              <div
                key={e.periode}
                className="bg-white border border-purple-100 rounded px-1.5 py-0.5"
                data-testid={`ff-rep-mois-${e.periode}`}
              >
                <span className="text-gray-500">{e.periode}</span>{" "}
                <span className="font-semibold text-purple-700">{e.montant}</span>
              </div>
            ))}
          </div>
          {rep.montants_mensuels?.length > 12 && (
            <p className="text-[10px] text-gray-400 mt-1 italic">
              +{rep.montants_mensuels.length - 12} mois supplémentaires…
            </p>
          )}
          <V2TransitoireToggle ff={ff} onUpdated={onUpdated} />
          <Button
            size="sm"
            variant="ghost"
            className="mt-2 text-red-600 h-7 text-xs"
            onClick={disable}
            disabled={saving}
            data-testid="ff-rep-disable"
          >
            Désactiver la répartition
          </Button>
        </div>
      ) : (
        <>
          {!showConfig ? (
            <Button
              size="sm"
              variant="outline"
              className="text-xs border-indigo-300 text-indigo-700 hover:bg-indigo-50"
              onClick={() => setShowConfig(true)}
              data-testid="ff-rep-config"
            >
              + Répartir cette charge dans le temps
            </Button>
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded p-3 space-y-2" data-testid="ff-rep-form">
              <div>
                <Label className="text-xs">Type de répartition</Label>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  className="w-full mt-1 border border-gray-300 rounded px-2 py-1.5 text-sm"
                  data-testid="ff-rep-type"
                >
                  <option value="mensuel">Mensuel (1 mois)</option>
                  <option value="trimestriel">Trimestriel (3 mois)</option>
                  <option value="annuel">Annuel (12 mois)</option>
                  <option value="personnalise">Personnalisé (plage libre)</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Date début</Label>
                  <Input
                    type="date"
                    value={dateDebut}
                    onChange={(e) => setDateDebut(e.target.value)}
                    data-testid="ff-rep-debut"
                  />
                </div>
                {type === "personnalise" && (
                  <div>
                    <Label className="text-xs">Date fin</Label>
                    <Input
                      type="date"
                      value={dateFin}
                      onChange={(e) => setDateFin(e.target.value)}
                      data-testid="ff-rep-fin"
                    />
                  </div>
                )}
              </div>
              <div className="flex gap-2 pt-1">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowConfig(false)}
                  className="flex-1"
                >
                  Annuler
                </Button>
                <Button
                  size="sm"
                  onClick={apply}
                  disabled={saving}
                  className="flex-[2] bg-indigo-600 hover:bg-indigo-700 text-white"
                  data-testid="ff-rep-apply"
                >
                  {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
                  Appliquer la répartition
                </Button>
              </div>
              <p className="text-[10px] text-gray-500 italic">
                ℹ️ La répartition ne modifie pas la comptabilité (1 écriture à la date réelle).
                Elle sert uniquement aux statistiques et dashboards mensuels.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ============================================================
// V2 — Comptabilité transitoire (iter31)
// ============================================================
function V2TransitoireToggle({ ff, onUpdated }) {
  const rep = ff.repartition_analytique || {};
  const v2Actif = !!rep.comptabilite_v2;
  const [busy, setBusy] = useState(false);
  const [globalActif, setGlobalActif] = useState(null);

  useEffect(() => {
    let mounted = true;
    api.get("/config/transitoire").then((r) => {
      if (mounted) setGlobalActif(!!r.data?.actif);
    }).catch(() => mounted && setGlobalActif(false));
    return () => { mounted = false; };
  }, []);

  const activer = async () => {
    if (!window.confirm(
      "Activer la comptabilité V2 transitoire ?\n\n" +
      "→ Le montant total sera basculé du compte de charge vers 1300 « Actifs transitoires » " +
      "à la date de la facture.\n" +
      "→ N OD pending seront créées (1 par mois ventilé).\n" +
      "→ Vous matérialiserez chaque OD à la fin du mois correspondant via la page « OD périodiques »."
    )) return;
    setBusy(true);
    try {
      const r = await api.post(`/factures-fournisseurs/${ff.id}/repartition/activer-v2`);
      toast.success(`V2 activée — ${r.data.nb_od_generees} OD générées.`);
      onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur activation V2");
    } finally {
      setBusy(false);
    }
  };

  const desactiver = async () => {
    if (!window.confirm(
      "Désactiver la V2 transitoire ?\n\n" +
      "Les OD non matérialisées seront supprimées. Les OD déjà matérialisées sont CONSERVÉES " +
      "(pour les annuler il faut faire une contre-écriture manuelle)."
    )) return;
    setBusy(true);
    try {
      await api.post(`/factures-fournisseurs/${ff.id}/repartition/desactiver-v2`);
      toast.success("V2 désactivée");
      onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur désactivation V2");
    } finally {
      setBusy(false);
    }
  };

  if (globalActif === false) {
    return (
      <div className="mt-3 border-t border-purple-200 pt-2 text-[10px] text-gray-500 italic">
        ℹ️ Comptabilité V2 transitoire non activée globalement
        (Paramètres &gt; Comptabilité).
      </div>
    );
  }

  return (
    <div className="mt-3 border-t border-purple-200 pt-2" data-testid="ff-v2-toggle">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[11px]">
          <span className="font-semibold text-gray-800">Compta V2 transitoire</span>{" "}
          {v2Actif ? (
            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-[10px] ml-1">
              Active · 1300
            </Badge>
          ) : (
            <Badge variant="outline" className="text-[10px] ml-1 text-gray-500">Inactive</Badge>
          )}
          <p className="text-[10px] text-gray-500 mt-0.5">
            Matérialise la répartition en vraies écritures (1300 ↔ compte charge), conforme CO 957.
          </p>
        </div>
        {v2Actif ? (
          <Button
            size="sm" variant="outline"
            className="text-[11px] h-7 border-red-300 text-red-700 hover:bg-red-50"
            onClick={desactiver} disabled={busy}
            data-testid="ff-v2-desactiver"
          >
            Désactiver V2
          </Button>
        ) : (
          <Button
            size="sm"
            className="text-[11px] h-7 bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={activer} disabled={busy || ff.statut === "a_valider" || ff.statut === "brouillon"}
            data-testid="ff-v2-activer"
          >
            Activer V2
          </Button>
        )}
      </div>
    </div>
  );
}
